import { supabase } from "../supabase";
import type { SyscohadaCompteOption } from "../comptable/importBalance";
import { formatCompte8, COMPTE_NUMERO_LEN as OHADA_COMPTE_EXPORT_LEN } from "../comptable/formatCompte";

export type CompteTresorerieType = "banque" | "mobile_money" | "caisse";

/**
 * Sous-classes de trésorerie d’exploitation (classe 5 SYSCOHADA) : 52x, 53x, 54x, 56x, 57x, 58x.
 * Exclus : titres 50, la série 51xx « valeurs à encaisser » sauf 517x / 518x (mobile money & assimilés en pratique UEMOA), 55x, 59x.
 * Les comptes 55x restent possibles en saisie manuelle si le plan ne charge pas ou compte déjà créé.
 */
const PREFIXES_COMPTE_TRESORERIE_SYSCOHADA = ["52", "53", "54", "56", "57", "58"] as const;

/** 517 / 518 : hors filtre générique « 51 = effets à encaisser ». */
function estCompte517ou518Syscohada(d: string): boolean {
  return d.startsWith("517") || d.startsWith("518");
}

/** Longueur standard des numéros de compte dans l’app (plan OHADA étendu). */
export const OHADA_COMPTE_NUMERO_LEN = 8;

function digitsCompte(num: string): string {
  return (num ?? "").replace(/\D/g, "");
}

/** Complète à droite par des zéros jusqu’à 8 chiffres (ex. 521 → 52100000). Tronque si plus de 8. */
export function normaliserNumeroCompteOhada8Chiffres(raw: string): string {
  const d = digitsCompte(raw);
  if (!d) return "";
  if (d.length >= OHADA_COMPTE_NUMERO_LEN) return d.slice(0, OHADA_COMPTE_NUMERO_LEN);
  return d.padEnd(OHADA_COMPTE_NUMERO_LEN, "0");
}

/** True si le numéro relève d’une ligne de trésorerie « exploitable » du plan OHADA (≥ 3 chiffres significatifs hors zéros de fin), ou 55x (monnaie électronique). */
export function estNumeroCompteTresorerieSyscohada(numero: string): boolean {
  const d = digitsCompte(numero);
  const significatif = d.replace(/0+$/, "") || d;
  if (significatif.length < 3) return false;
  if (d.startsWith("50") || d.startsWith("59")) return false;
  if (d.startsWith("51") && !estCompte517ou518Syscohada(d)) return false;
  if (d.startsWith("55")) return true;
  if (estCompte517ou518Syscohada(d)) return true;
  return PREFIXES_COMPTE_TRESORERIE_SYSCOHADA.some((p) => d.startsWith(p));
}

/** Filtre le plan SYSCOHADA (`syscohada_comptes_options.json`) pour le paramétrage des comptes de trésorerie. */
export function filterSyscohadaComptesTresorerie(comptes: SyscohadaCompteOption[]): SyscohadaCompteOption[] {
  return comptes.filter((c) => {
    if (c.classe !== 5) return false;
    const d = digitsCompte(String(c.code ?? c.value ?? ""));
    if (d.length < 3) return false;
    if (d.startsWith("50") || d.startsWith("59")) return false;
    if (d.startsWith("51") && !estCompte517ou518Syscohada(d)) return false;
    if (estCompte517ou518Syscohada(d)) return true;
    return PREFIXES_COMPTE_TRESORERIE_SYSCOHADA.some((p) => d.startsWith(p));
  });
}

/** Options pour le sélecteur : numéro toujours sur 8 chiffres, libellé « 52100000 - Libellé ». */
export function buildSyscohadaTresorerieSelectOptions(
  comptes: SyscohadaCompteOption[]
): { value: string; label: string; libelle: string }[] {
  const filtered = filterSyscohadaComptesTresorerie(comptes);
  const seen = new Set<string>();
  const out: { value: string; label: string; libelle: string }[] = [];
  for (const o of filtered) {
    const value = normaliserNumeroCompteOhada8Chiffres(String(o.value ?? o.code ?? ""));
    if (!value || seen.has(value)) continue;
    seen.add(value);
    const libelle = (o.name ?? "").trim() || value;
    out.push({ value, libelle, label: `${value} - ${libelle}` });
  }
  out.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
  return out;
}

export type CompteTresorerieRow = {
  id: string;
  dossier_id: string;
  numero: string;
  libelle: string;
  type: CompteTresorerieType;
  actif: boolean;
  created_at?: string;
  updated_at?: string;
};

export type SaisieOperationLine = {
  date: string;
  libelle: string;
  debit: number | null;
  credit: number | null;
};

export function operateurFromCompteType(t: CompteTresorerieType): "banque" | "manuel" {
  return t === "banque" ? "banque" : "manuel";
}

export async function fetchComptesTresorerieActifs(dossierId: string): Promise<CompteTresorerieRow[]> {
  const { data, error } = await supabase
    .from("comptes_tresorerie")
    .select("*")
    .eq("dossier_id", dossierId)
    .eq("actif", true)
    .order("libelle");
  if (error) throw error;
  return (data || []) as CompteTresorerieRow[];
}

export async function fetchComptesTresorerie(dossierId: string): Promise<CompteTresorerieRow[]> {
  const { data, error } = await supabase
    .from("comptes_tresorerie")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("actif", { ascending: false })
    .order("libelle");
  if (error) throw error;
  return (data || []) as CompteTresorerieRow[];
}

export async function insertCompteTresorerie(
  dossierId: string,
  input: { numero: string; libelle: string; type: CompteTresorerieType; actif?: boolean }
): Promise<CompteTresorerieRow> {
  const { data, error } = await supabase
    .from("comptes_tresorerie")
    .insert({
      dossier_id: dossierId,
      numero: input.numero.trim(),
      libelle: input.libelle.trim(),
      type: input.type,
      actif: input.actif ?? true,
    })
    .select()
    .single();
  if (error) throw error;
  return data as CompteTresorerieRow;
}

export async function updateCompteTresorerie(
  id: string,
  patch: Partial<Pick<CompteTresorerieRow, "numero" | "libelle" | "type" | "actif">>
): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.numero != null) row.numero = patch.numero.trim();
  if (patch.libelle != null) row.libelle = patch.libelle.trim();
  if (patch.type != null) row.type = patch.type;
  if (patch.actif != null) row.actif = patch.actif;
  const { error } = await supabase.from("comptes_tresorerie").update(row).eq("id", id);
  if (error) throw error;
}

export async function deleteCompteTresorerie(id: string): Promise<void> {
  const { error } = await supabase.from("comptes_tresorerie").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Crée des transactions à partir de la grille / import.
 * Convention alignée sur l’app : montant_ttc > 0, `type` = sortie (débit) ou entree (crédit).
 * `statut_justification` = non_justifiee ; compte/libellé = compte de trésorerie sélectionné.
 */
export async function createTransactionsFromSaisie(
  dossierId: string,
  compte: CompteTresorerieRow,
  lignes: SaisieOperationLine[]
): Promise<number> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Non connecté");

  const operateur = operateurFromCompteType(compte.type);
  const numeroTreso8 = formatCompte8(compte.numero.trim());
  const rows: Record<string, unknown>[] = [];

  for (const L of lignes) {
    const d = L.debit != null && L.debit > 0 ? L.debit : null;
    const c = L.credit != null && L.credit > 0 ? L.credit : null;
    if (d != null && c != null) continue;
    if (d == null && c == null) continue;
    const type = d != null ? "sortie" : "entree";
    const montant_ttc = d != null ? d : c!;
    rows.push({
      user_id: userData.user.id,
      dossier_id: dossierId,
      date: L.date,
      libelle: L.libelle.trim() || "Opération",
      montant_ttc,
      type,
      operateur,
      statut: "non_affectee",
      statut_justification: "non_justifiee",
      compte_comptable_treso: numeroTreso8 || compte.numero.trim(),
      libelle_comptable_treso: compte.libelle.trim(),
    });
  }

  if (rows.length === 0) return 0;

  const { error } = await supabase.from("transactions").insert(rows);
  if (error) throw error;
  return rows.length;
}

/** Logos publics (mêmes chemins que la page Transactions) pour comptes mobile money du plan. */
export const LOGO_COMPTE_TRESORERIE_MOBILE_MONEY: Record<string, string> = {
  "51710000": "/logo-orangemoney.png",
  "51720000": "/logo-momo.png",
  "51730000": "/logo-wave.png",
};

export function logoUrlPourCompteTresorerieNumero(numero: string): string | null {
  const k = normaliserNumeroCompteOhada8Chiffres(numero.trim());
  return LOGO_COMPTE_TRESORERIE_MOBILE_MONEY[k] ?? null;
}
